import type {
  ArtifactContentChartschema,
  MetricsIntentQueryRangeRequest,
  MetricsSeriesResponse
} from '@app/types/sdk';

export type ChartArtifactContent = ArtifactContentChartschema;

export type ChartRow = Record<string, unknown>;

export type ChartDatasetMap = Record<string, ChartRow[]>;

export type VegaLiteSpec = Record<string, unknown>;

export type ChartSpecBuildResult =
  | { ok: true; spec: VegaLiteSpec; warnings: string[]; datasetNames: string[] }
  | { ok: false; error: string };

const cloneJSON = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const coerceString = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);

const coerceDate = (value: unknown): Date | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export const defaultDataRefRep = (ref: { rep?: string }): string => {
  const rep = String(ref.rep ?? '').trim();
  return rep || 'table:result';
};

export const datasetNameForRef = (ref: { name?: string }, index: number, spec?: VegaLiteSpec): string => {
  const explicit = coerceString(ref.name);
  if (explicit) {
    return explicit;
  }
  if (index === 0 && isRecord(spec?.data)) {
    const fromSpec = coerceString((spec.data as Record<string, unknown>).name);
    if (fromSpec) {
      return fromSpec;
    }
  }
  return index === 0 ? 'main' : `dataset_${index + 1}`;
};

export const staticDatasetRowsFromSnapshots = (chart: ChartArtifactContent): ChartDatasetMap => {
  const snapshot = chart.lastSnapshot as any;
  const datasetName = coerceString(snapshot?.datasetName);
  const rows = Array.isArray(snapshot?.rows) ? (snapshot.rows as ChartRow[]) : [];
  return datasetName && rows.length > 0 ? { [datasetName]: rows } : {};
};

export const buildChartSpec = (chart: ChartArtifactContent, datasets: ChartDatasetMap): ChartSpecBuildResult => {
  if (!chart || !isRecord(chart.spec)) {
    return { ok: false, error: 'chart.spec must be an object' };
  }

  const spec = cloneJSON(chart.spec as VegaLiteSpec);
  const warnings: string[] = [];
  const datasetNames = Object.keys(datasets);

  const data = isRecord(spec.data) ? (spec.data as Record<string, unknown>) : {};
  if (data.url != null) {
    return { ok: false, error: 'chart.spec.data.url is not allowed; data must be resolved by the UI' };
  }

  const existingDatasets = isRecord(spec.datasets) ? (spec.datasets as Record<string, unknown>) : {};
  spec.datasets = { ...existingDatasets, ...datasets };

  if (datasetNames.length > 0) {
    const activeDataName = coerceString(data.name) ?? datasetNames[0];
    spec.data = { ...data, name: activeDataName };
  }

  if (data.values != null) {
    warnings.push('chart.spec.data.values is present; UI injected datasets may override it');
  }

  return { ok: true, spec, warnings, datasetNames };
};

export const chartPrimaryDatasetName = (chart: ChartArtifactContent): string => {
  const bindingName = coerceString((chart.dataBinding as any)?.datasetName);
  if (bindingName) {
    return bindingName;
  }
  const snapshotName = coerceString((chart.lastSnapshot as any)?.datasetName);
  if (snapshotName) {
    return snapshotName;
  }
  if (isRecord(chart.spec?.data)) {
    const specName = coerceString((chart.spec.data as Record<string, unknown>).name);
    if (specName) {
      return specName;
    }
  }
  const firstRef = Array.isArray(chart.dataRefs) ? chart.dataRefs[0] : undefined;
  return firstRef ? datasetNameForRef(firstRef, 0, chart.spec as VegaLiteSpec) : 'main';
};

export const buildMetricsRangeRequest = (chart: ChartArtifactContent, now: Date = new Date()): MetricsIntentQueryRangeRequest => {
  const binding = chart.dataBinding as any;
  if (!binding || binding.source !== 'metrics') {
    throw new Error('chart.dataBinding.source must be metrics');
  }
  const metrics = binding.metrics;
  if (!metrics || typeof metrics !== 'object') {
    throw new Error('chart.dataBinding.metrics is required');
  }
  if (metrics.capabilityRef) {
    throw new Error('chart.dataBinding.metrics.capabilityRef is not supported by this UI version');
  }
  if (!metrics.intent && !metrics.queryPlan) {
    throw new Error('chart.dataBinding.metrics must include intent or queryPlan');
  }
  if (!metrics.scope) {
    throw new Error('chart.dataBinding.metrics.scope is required');
  }

  const time = metrics.time;
  if (!time || typeof time !== 'object') {
    throw new Error('chart.dataBinding.metrics.time is required');
  }
  const stepSec = Number(time.stepSec);
  if (!Number.isFinite(stepSec) || stepSec <= 0) {
    throw new Error('chart.dataBinding.metrics.time.stepSec must be positive');
  }

  let from: Date;
  let to: Date;
  if (time.mode === 'rolling') {
    const lookbackSeconds = Number(time.lookbackSeconds);
    if (!Number.isFinite(lookbackSeconds) || lookbackSeconds <= 0) {
      throw new Error('chart.dataBinding.metrics.time.lookbackSeconds must be positive for rolling charts');
    }
    to = now;
    from = new Date(to.getTime() - lookbackSeconds * 1000);
  } else if (time.mode === 'fixed') {
    from = coerceDate(time.from) ?? new Date(NaN);
    to = coerceDate(time.to) ?? new Date(NaN);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      throw new Error('chart.dataBinding.metrics.time.from and to are required for fixed charts');
    }
  } else {
    throw new Error('chart.dataBinding.metrics.time.mode must be fixed or rolling');
  }

  return {
    intent: metrics.intent || undefined,
    queryPlan: metrics.queryPlan || undefined,
    scope: metrics.scope,
    selectors: metrics.selectors,
    range: { from, to, stepSec },
    asOf: coerceDate(metrics.asOf) ?? undefined
  } as MetricsIntentQueryRangeRequest;
};

export const metricsSeriesToRows = (response: MetricsSeriesResponse, preferredLabelFields: string[] = []): ChartRow[] => {
  const seriesList = Array.isArray(response.series) ? response.series : [];
  return seriesList.flatMap((series, seriesIndex) => {
    const labels = isRecord(series.labels) ? (series.labels as Record<string, string>) : {};
    const labelParts = preferredLabelFields
      .map((field) => labels[field])
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    const fallbackLabel = Object.entries(labels)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}=${value}`)
      .join(', ');
    const displaySeries = labelParts.length > 0 ? labelParts.join(' / ') : fallbackLabel || `series ${seriesIndex + 1}`;
    const values = Array.isArray(series.values) ? series.values : [];
    return values.map((sample) => ({
      ...labels,
      t: sample.t instanceof Date ? sample.t.toISOString() : sample.t,
      value: sample.value,
      series: displaySeries
    }));
  });
};
